import { useWebsiteSettings } from 'client/store'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateStoreObject } from 'src/editor/shared/hooks/use-update-store'
import { setWebsiteDraftTheme } from 'src/editor/store/website-settings/website-settings-actions'
import {
  getWebsiteDraftTheme,
  getWebsiteTheme,
} from 'src/editor/store/website-settings/website-settings-selector'
import { WebsiteThemeInterface } from 'src/shared/types/website-settings-types'

export const useCurrentTheme = () => {
  const { t } = useTranslation()
  const savedTheme = useWebsiteSettings(
    getWebsiteTheme,
  ) as WebsiteThemeInterface

  const draftTheme = useWebsiteSettings(
    getWebsiteDraftTheme,
  ) as WebsiteThemeInterface | null

  const currentTheme = draftTheme ?? savedTheme

  const [activeThemeId, setActiveThemeId] = useState<number | null>(
    currentTheme?.id ?? null,
  )

  const updateDraftTheme = useUpdateStoreObject(setWebsiteDraftTheme)

  const onReset = useCallback(() => {
    updateDraftTheme(null)
    setActiveThemeId(savedTheme.id ?? null)
  }, [updateDraftTheme])

  const onSelectTheme = useCallback((theme: WebsiteThemeInterface) => {
    if (theme.id === savedTheme.id) {
      updateDraftTheme(null)
      return
    }
    updateDraftTheme(theme)
  }, [])

  const onUpdate = useCallback(
    (updatedFields: Partial<WebsiteThemeInterface>) => {
      if (!currentTheme) {
        return
      }
      const updatedTheme: WebsiteThemeInterface = {
        ...currentTheme,
        ...updatedFields,
      }

      if (currentTheme.isCommon) {
        const copiedTheme: WebsiteThemeInterface = {
          ...updatedTheme,
          id: null,
          isCommon: false,
          name: `${t('global.new')} ${draftTheme?.name ?? t('website_settings.sidebar.theme')}`,
        }
        updateDraftTheme(copiedTheme)
      } else {
        updateDraftTheme(updatedTheme)
      }
    },
    [currentTheme, updateDraftTheme, savedTheme],
  )

  useEffect(() => {
    if (activeThemeId === null && currentTheme) {
      const newTheme: WebsiteThemeInterface = {
        ...currentTheme,
        id: null,
        isCommon: false,
        name: 'New theme',
      }
      updateDraftTheme(newTheme)
    }
  }, [activeThemeId])

  return {
    activeThemeId,
    setActiveThemeId,
    savedTheme,
    draftTheme,
    currentTheme,
    onReset,
    onUpdate,
    onSelectTheme,
  }
}
